import { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Text,
  Loader,
  Notification,
  Center,
} from "@mantine/core";
import "./DemoSubHeader.scss";
import DemoSubCard from "./DemoSubCard/DemoSubCard";
import { demoData, EVENT_IDS } from "./utils";
import { fetchAllWebinarData } from "../../../../utils/api/resources";
import { onboarding_url } from "../../../../constants";
import { openNewTab } from "@/utils/browserUtils";

const DemoSubHeader = () => {
  // State management
  const [isTaxTuesdayModalOpen, setTaxTuesdayModalOpen] = useState(false);
  const [isWildWednesdayModalOpen, setWildWednesdayModalOpen] = useState(false);
  const [loadingWebinarData, setLoadingWebinarData] = useState(true);
  const [webinarData, setWebinarData] = useState<any>(null);
  const [errorWebinarData, setErrorWebinarData] = useState<string | null>(null);

  // Fetch webinar data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoadingWebinarData(true);
      try {
        const data = await fetchAllWebinarData();
        setWebinarData(data);
      } catch (error) {
        setErrorWebinarData("Failed to fetch webinar data.");
      } finally {
        setLoadingWebinarData(false);
      }
    };

    fetchData();
  }, []);

  // Function to get webinar data for a specific event
  const getWebinarDataForEvent = (eventType: string) => {
    if (!webinarData) return null;
    const eventId = EVENT_IDS[eventType];
    if (!eventId) return null;

    return webinarData[String(eventId)] || null;
  };

  // Handle button clicks based on the buttonHandler string
  const handleButtonClick = (handler: string) => {
    switch (handler) {
      case "openCalendlyURL":
        openNewTab(onboarding_url);
        break;
      case "openTaxTuesdayModal":
        setTaxTuesdayModalOpen(true);
        break;
      case "openWildWednesdayModal":
        setWildWednesdayModalOpen(true);
        break;
      default:
        break;
    }
  };

  // Handle event registration
  const handleRegisterEvent = (eventType: string) => {
    const eventData = getWebinarDataForEvent(eventType);

    if (!eventData?.registrationUrl) {
      console.error("Event data not found.");
      return;
    }

    // Open the registration URL in a new tab
    openNewTab(eventData.registrationUrl);

    // Close the modal
    if (eventType === "taxTuesday") {
      setTaxTuesdayModalOpen(false);
    } else if (eventType === "wildWednesday") {
      setWildWednesdayModalOpen(false);
    }
  };

  // Extracted rendering logic for modals
  const renderModalContent = (eventType: string, closeModal: () => void) => {
    if (loadingWebinarData) {
      return (
        <Center>
          <Loader size="sm" />
          <Text ml="sm">Fetching event details...</Text>
        </Center>
      );
    }

    if (errorWebinarData) {
      return (
        <Notification color="red" title="Error">
          {errorWebinarData}
        </Notification>
      );
    }

    const data = getWebinarDataForEvent(eventType);
    if (data?.nextOccurrence) {
      return (
        <>
          <Text>
            You will be redirected to the registration page for the event
            on{" "}
          </Text>
          <Text fw={500} c="#0077cc" m={"lg"}>
            {new Date(data.nextOccurrence).toLocaleString()}
          </Text>
          <div className="demo-register-btns">
            <Button color="#0077cc" variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              color="#0077cc"
              onClick={() => handleRegisterEvent(eventType)}
            >
              Register
            </Button>
          </div>
        </>
      );
    }

    return <Text>No data available. Please try again later.</Text>;
  };

  return (
    <div className="demo-sub-header">
      {demoData.map((data, index) => {
        let extraText = "";
        if (data.eventType) {
          if (loadingWebinarData) {
            extraText = "Fetching next event date...";
          } else if (errorWebinarData) {
            extraText = "Failed to fetch event date.";
          } else {
            const eventData = getWebinarDataForEvent(data.eventType);
            if (eventData?.nextOccurrence) {
              extraText = `Join Us: ${new Date(eventData.nextOccurrence).toLocaleString()}`;
            } else {
              extraText = "No upcoming events.";
            }
          }
        }
        return (
          <div key={`${index}-${extraText}`}>
            <DemoSubCard
              {...data}
              extraText={extraText}
              buttonHandler={() => handleButtonClick(data.buttonHandler)}
            />
          </div>
        );
      })}

      {/* Tax Tuesday Modal */}
      <Modal
        opened={isTaxTuesdayModalOpen}
        onClose={() => setTaxTuesdayModalOpen(false)}
        title={
          <Text fw={500} size="xl">
            Join SmartWiz Tax Tuesdays?
          </Text>
        }
      >
        {renderModalContent("taxTuesday", () => setTaxTuesdayModalOpen(false))}
      </Modal>

      {/* Wild Wednesday Modal */}
      <Modal
        opened={isWildWednesdayModalOpen}
        onClose={() => setWildWednesdayModalOpen(false)}
        title={
          <Text fw={500} size="xl">
            Join Wild Wednesdays?
          </Text>
        }
      >
        {renderModalContent("wildWednesday", () =>
          setWildWednesdayModalOpen(false)
        )}
      </Modal>
    </div>
  );
};

export default DemoSubHeader;
